import { useCallback, useEffect, useRef, useState } from 'react';
import { readOverview } from '../api';
import type { OverviewReply } from '../api-types';
import { clearDriveToken, getDriveToken } from '../drive-token';

export type OverviewState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly reply: OverviewReply }
  | { readonly status: 'consent' }
  | { readonly status: 'unreachable'; readonly httpStatus: number };

/**
 * The overview of one folder (`path`), kept while it reloads: a refresh swaps the data in without
 * blanking the page. A 428 DRIVE_CONSENT_REQUIRED drops the token and asks for the consent again.
 */
export function useOverview(path: string | undefined) {
  const [state, setState] = useState<OverviewState>({ status: 'loading' });
  const run = useRef(0);

  const load = useCallback(
    async (silent: boolean) => {
      const mine = ++run.current;
      if (!silent) setState({ status: 'loading' });
      const answer = await readOverview(path, getDriveToken());
      if (mine !== run.current) return;
      if (answer.ok) {
        setState({ status: 'ready', reply: answer.value });
      } else if (answer.status === 428 && answer.code === 'DRIVE_CONSENT_REQUIRED') {
        clearDriveToken();
        setState({ status: 'consent' });
      } else {
        setState({ status: 'unreachable', httpStatus: answer.status });
      }
    },
    [path],
  );

  useEffect(() => {
    void load(false);
    return () => {
      run.current += 1;
    };
  }, [load]);

  const reload = useCallback(() => void load(true), [load]);
  const retry = useCallback(() => void load(false), [load]);
  return { state, reload, retry };
}
